import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';

import GameplaySession from '../../../../Model/GameplaySession';
import TrackedActivity from '../../../../Model/TrackedActivity';
import WorkoutFeedView from '../../../Model/WorkoutFeedView';
import { WorkoutFeedViewType } from '../utils';
import { getWorkoutKey, getWorkoutKeys, isHealthLinkable } from './workoutHealthData';

const DAY_MS = 24 * 60 * 60 * 1000;
// Sessions can start before or after the day a workout is scheduled on.
const SESSION_LOOKBEHIND_MS = DAY_MS;
const SESSION_LOOKAHEAD_MS = 2 * DAY_MS;

const EMPTY = new Map();

// The tracked activity of each check-in workout, by workout key. Check-in rows have no ids, so the
// week's feed rows supply them.
const useWorkoutHealthData = (clientId, coachId, workoutList) => {
  const [healthByWorkoutKey, setHealthByWorkoutKey] = useState(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  // Refetch when the workouts change, not whenever the parent rebuilds the array.
  const workoutListRef = useRef(workoutList);
  workoutListRef.current = workoutList;
  const workoutListKey = workoutList.map(getWorkoutKey).join('\n');

  useEffect(() => {
    const workouts = workoutListRef.current;
    const linkableWorkouts = workouts.filter(isHealthLinkable);
    if (!clientId || !linkableWorkouts.length) {
      setHealthByWorkoutKey(EMPTY);
      return undefined;
    }

    let isCancelled = false;
    const loadHealthData = async () => {
      setIsLoading(true);
      try {
        const startTimes = workouts.map((workout) => workout.startDate.toDate().getTime());
        const rangeStart = Math.min(...startTimes);
        const rangeEnd = Math.max(...startTimes);
        // The check-in lists only this coach's assigned workouts; other coaches' rows would shift the matching.
        const isOtherCoachsWorkout = (row) => row.type === WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT
          && !!row.coach && row.coach !== coachId;
        const weekRows = await WorkoutFeedView.getByUserInDateRange(clientId, new Date(rangeStart), new Date(rangeEnd));
        const feedRows = weekRows.filter((row) => !isOtherCoachsWorkout(row));
        const feedRowByKey = new Map([...getWorkoutKeys(feedRows.map((row) => ({
          row, type: row.type, startDate: row.startDate, title: row.title,
        })))].map(([{ row }, key]) => [key, row]));
        const workoutKeys = getWorkoutKeys(workouts);

        const result = new Map();
        const keyByAssignmentId = new Map();
        const directKeys = [];
        linkableWorkouts.forEach((workout) => {
          const key = workoutKeys.get(workout);
          const feedRow = feedRowByKey.get(key);
          if (!feedRow) return;
          if (workout.type === WorkoutFeedViewType.TRACKED_ACTIVITY) {
            directKeys.push([key, feedRow.id]);
          } else if (feedRow.trackedActivity) {
            directKeys.push([key, feedRow.trackedActivity]);
          } else {
            keyByAssignmentId.set(feedRow.id, key);
          }
        });

        const loadDirect = Promise.all(directKeys.map(async ([key, id]) => {
          const activity = await TrackedActivity.getById(id);
          if (activity) result.set(key, activity);
        }));

        const loadInApp = (async () => {
          if (!keyByAssignmentId.size) return;
          const sessions = await GameplaySession.getByUserInTimeRange(
            clientId,
            rangeStart - SESSION_LOOKBEHIND_MS,
            rangeEnd + SESSION_LOOKAHEAD_MS,
          );
          const assignmentBySessionId = new Map(sessions
            .filter((session) => keyByAssignmentId.has(session.workoutAssignment))
            .map((session) => [session.id, session.workoutAssignment]));
          if (!assignmentBySessionId.size) return;
          const activities = await TrackedActivity.getByGameplaySessions(clientId, [...assignmentBySessionId.keys()]);
          activities.forEach((activity) => {
            const key = keyByAssignmentId.get(assignmentBySessionId.get(activity.gameplaySession));
            // A workout repeated in several sessions keeps its longest recording.
            if (key && (!result.has(key) || result.get(key).duration < activity.duration)) {
              result.set(key, activity);
            }
          });
        })();

        await Promise.all([loadDirect, loadInApp]);
        if (!isCancelled) setHealthByWorkoutKey(result);
      } catch (error) {
        Sentry.captureException(error);
        if (!isCancelled) setHealthByWorkoutKey(EMPTY);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    loadHealthData();

    return () => {
      isCancelled = true;
    };
  }, [clientId, coachId, workoutListKey]);

  return { healthByWorkoutKey, isLoading };
};

export default useWorkoutHealthData;
